import type { Propfirm } from './types';

// Données propfirms embarquées (comparateur neutre — ni affiliation, ni promo).
// Source : dataset de mission vérifié le 2026-07-24, avec les 16 corrections
// d'audit appliquées (voir notes/sources par firme). Vague 2 (vérifiée le
// 2026-07-26) : 16 propfirms supplémentaires, ses 8 corrections d'audit
// appliquées, notes de prudence conservées. `logoUrl: null` partout :
// l'utilisateur complétera les logos (fallback avatar initiales + favicon runtime).
// Plateformes propriétaires non standard (FTUK XT, terminaux maison…) normalisées
// en `'autre'` conformément à l'union fermée `PlatformId`.

export const PROPFIRMS: readonly Propfirm[] = [
  {
    name: 'FTMO',
    slug: 'ftmo',
    category: 'forex',
    notorietyRank: 1,
    website: 'https://ftmo.com',
    logoUrl: null,
    foundedYear: 2015,
    headquarters: 'Prague, Tchéquie',
    regulation: [],
    platforms: ['MT4', 'MT5', 'cTrader', 'DXtrade'],
    trustpilotScore: 4.8,
    trustpilotReviews: 46000,
    profitSplitMaxPct: 90,
    payoutFrequency: 'bi-mensuel (14j), on-demand',
    maxAllocation: 400000,
    rules: {
      minTradingDays: 4,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: false,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA et algorithmes admis sans validation préalable, y compris ceux du commerce.',
          'Un EA du commerce peut être joué par d’autres traders : le plafond de 400 000 $ par stratégie s’applique alors à l’ensemble, et le compte FTMO peut être refusé.',
          'Un EA qui dépasse 2 000 requêtes serveur par jour doit être réglé.',
          'Copie admise entre vos propres comptes ; donner l’accès du compte à un tiers est interdit.',
        ],
        sources: [
          'https://ftmo.com/en/faq/which-instruments-can-i-trade-and-what-strategies-am-i-allowed-to-use/',
          'https://ftmo.com/en/forbidden-trading-practices/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'Best Day Rule 50% (1-step uniquement)',
    },
    accounts: [
      { label: 'FTMO Challenge 2-Step 10K', model: '2-step', sizeUsd: 10000, priceUsd: 155, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'FTMO Challenge 2-Step 50K', model: '2-step', sizeUsd: 50000, priceUsd: 345, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'FTMO Challenge 2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 540, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'FTMO Challenge 2-Step 200K', model: '2-step', sizeUsd: 200000, priceUsd: 1080, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'FTMO Challenge 1-Step 10K', model: '1-step', sizeUsd: 10000, priceUsd: 79, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 10, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: true },
      { label: 'FTMO Challenge 1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 499, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 10, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: true },
    ],
    notes: "Facturation en EUR (79-1080 EUR), montants USD approx. 1-step lancé fév. 2026. News/weekend OK sur variante Swing. Scaling jusqu'à 2M$.",
    sources: [
      'https://ftmo.com/en/trading-objectives/',
      'https://thetrustedprop.com/prop-firms/ftmo',
      'https://proptradingvibes.com/prop-firms/ftmo',
      'https://propfirmkey.com/en/blog/ftmo-challenge-cost-2026',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FundedNext',
    slug: 'fundednext',
    category: 'forex',
    notorietyRank: 2,
    website: 'https://fundednext.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'Dubaï, EAU',
    regulation: [],
    platforms: ['MT4', 'MT5', 'cTrader', 'Match-Trader'],
    trustpilotScore: 4.5,
    trustpilotReviews: 74041,
    profitSplitMaxPct: 95,
    payoutFrequency: 'hebdo (1-step) à bi-mensuel',
    maxAllocation: 300000,
    rules: {
      minTradingDays: 5,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'conditional',
        copyOwnAccounts: 'conditional',
        notes: [
          'EA admis sur MT4 et MT5 seulement, sur les comptes de moins de 50 000 $, avec l’option EA payante ; interdits sur cTrader et Match-Trader, et sur les comptes de 50 000 $ et plus.',
          'Réglages propres à chaque trader, 300 000 $ au plus par stratégie, aucun EA relié à Telegram ou WhatsApp, aucun EA vendu pour passer les challenges.',
          'Copie admise entre vos comptes Challenge seulement : dès qu’un compte financé FundedNext est d’un côté, elle est interdite.',
          'Les services de copie « cloud » tiers (Social Trader Tools, Traders Connect, Duplikum) sont bannis.',
        ],
        sources: [
          'https://help.fundednext.com/en/articles/8020763-is-ea-allowed-in-fundednext',
          'https://help.fundednext.com/en/articles/8019805-what-is-the-copy-trading-rule-at-fundednext',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'aucune en challenge; cap 40% profits news en funded',
    },
    accounts: [
      { label: 'Stellar 2-Step 25K', model: '2-step', sizeUsd: 25000, priceUsd: 199.99, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Stellar 2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 549.99, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Stellar 2-Step 200K', model: '2-step', sizeUsd: 200000, priceUsd: 1099.99, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Stellar 1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 569.99, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Stellar Lite 100K', model: '2-step', sizeUsd: 100000, priceUsd: 399.99, targetP1Pct: 8, targetP2Pct: 4, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Stellar Instant 10K', model: 'instant', sizeUsd: 10000, priceUsd: 299.99, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: null, refundableFee: false },
    ],
    notes: "Payout garanti sous 24h (compensation 1000$). Scaling jusqu'à 4M$. Tailles atypiques 6K/15K sur Stellar. Entité GrowthNext FZC.",
    sources: [
      'https://thetrustedprop.com/prop-firms/funded-next',
      'https://proptradingvibes.com/blog/fundednext-pricing',
      'https://propfirmmap.com/firms/fundednext',
      'https://www.trustpilot.com/review/fundednext.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FundingPips',
    slug: 'fundingpips',
    category: 'forex',
    notorietyRank: 3,
    website: 'https://fundingpips.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'Dubaï, EAU',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'Match-Trader'],
    trustpilotScore: 4.5,
    trustpilotReviews: 62062,
    profitSplitMaxPct: 100,
    payoutFrequency: 'hebdo (mardi), options on-demand',
    maxAllocation: null,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Automatisation complète admise avec votre propre EA, preuve de propriété à l’appui (code source, historique de développement) ; un binaire compilé ne suffit pas.',
          'Un EA tiers est admis comme gestionnaire de trade ou de risque — l’usage qu’en fait le terminal. S’en servir pour trader à votre place fait refuser l’évaluation ou le paiement.',
          'Copie admise entre vos propres comptes FundingPips, et de FundingPips vers un compte externe ; jamais depuis un compte externe.',
          'Tous les EA sont interdits en compétition mensuelle ; le compte 1K Instant admet au contraire EA tiers et copieurs.',
        ],
        sources: [
          'https://help.fundingpips.com/hc/en-us/articles/34505029138449-Trading-Conduct-and-Security-Standards',
          'https://fundingpips.com/terms-and-conditions',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '35% si payout on-demand; 15% sur Zero',
    },
    accounts: [
      { label: '2-Step 5K', model: '2-step', sizeUsd: 5000, priceUsd: 36, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Step 25K', model: '2-step', sizeUsd: 25000, priceUsd: 156, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 529, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 555, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Step Pro 100K', model: '2-step', sizeUsd: 100000, priceUsd: 399, targetP1Pct: 6, targetP2Pct: 6, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Zero (instant) 100K', model: 'instant', sizeUsd: 100000, priceUsd: 499, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 5, drawdownType: 'trailing', profitSplitPct: 95, refundableFee: false },
    ],
    notes: "Prix en hausse en 2026 (100K 2-step passé de 399$ à 529$). Hot Seat: split 100% et scaling jusqu'à 2M$. News interdit sur Zero.",
    sources: [
      'https://propfirmmap.com/firms/fundingpips',
      'https://www.fxempire.com/prop-firms/fundingpips',
      'https://proptradingvibes.com/prop-firms/fundingpips',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'The5ers',
    slug: 'the5ers',
    category: 'forex',
    notorietyRank: 5,
    website: 'https://the5ers.com',
    logoUrl: null,
    foundedYear: 2016,
    headquarters: 'Israël',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'TradingView'],
    trustpilotScore: 4.7,
    trustpilotReviews: 26643,
    profitSplitMaxPct: 100,
    payoutFrequency: 'bi-mensuel (14j)',
    maxAllocation: null,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'Tout EA est admis à condition d’en posséder le code source ; un EA tiers qui ouvre les mêmes trades chez d’autres traders est traité comme de la copie.',
          'Le stop-loss doit être visible sur la plateforme : un stop « furtif » géré par l’EA est interdit.',
          'La règle du code source vise tout EA : faire confirmer par le support qu’un EA de gestion tiers est admis.',
          'Copie de vos propres trades admise entre tous vos comptes ; plusieurs comptes Bootcamp doivent porter des stratégies différentes.',
        ],
        sources: [
          'https://the5ers.com/faqs/can-i-use-an-ea-expert-advisor-can-i-set-a-stealth-mode-stop-loss/',
          'https://www.the5ers.com/faqs/prohibited-trading-practices/',
          'https://the5ers.com/faqs/can-i-copy-my-own-trades/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: null,
    },
    accounts: [
      { label: 'High Stakes 5K', model: '2-step', sizeUsd: 5000, priceUsd: 39, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'High Stakes 100K', model: '2-step', sizeUsd: 100000, priceUsd: 495, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Bootcamp 3-Step (entrée)', model: '3-step', sizeUsd: 20000, priceUsd: 95, targetP1Pct: 6, targetP2Pct: 6, maxDailyLossPct: 5, maxDrawdownPct: 5, drawdownType: 'static', profitSplitPct: 50, refundableFee: null },
    ],
    notes: "3 jours profitables min par phase. Scaling par paliers de 10% jusqu'à 4M$, split 80 vers 100%. Hyper Growth (instant) en refonte, tarifs non confirmés. News: pas d'ordre a moins de 2 min d'une annonce. Gamme High Stakes actuelle : 2.5K/5K/10K/25K/50K/100K (fee remboursé au 1er payout).",
    sources: [
      'https://the5ers.com/high-stakes/',
      'https://the5ers.com/bootcamp/',
      'https://tradetanto.com/learn/the-5-ers-rules-explained-a-complete-guide',
      'https://propfirmapp.com/prop-firms/the5ers',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Alpha Capital Group',
    slug: 'alpha-capital-group',
    category: 'forex',
    notorietyRank: 6,
    website: 'https://alphacapitalgroup.uk',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Londres, UK',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'DXtrade', 'TradeLocker'],
    trustpilotScore: 4.7,
    trustpilotReviews: 20000,
    profitSplitMaxPct: 80,
    payoutFrequency: 'bi-mensuel, on-demand',
    maxAllocation: 400000,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: true,
      algo: {
        automated: 'forbidden',
        tradeManager: 'conditional',
        copyOwnAccounts: 'conditional',
        notes: [
          'Seuls les EA de gestion de trade ou de risque sont approuvés (taille de lot, stop et objectif, break-even) : un EA qui trade seul n’est approuvé en aucun cas.',
          'MT5 uniquement, EA à faire approuver avant usage (fichier EX5 au support) ; rien sur cTrader, DXtrade, TradeLocker, ni sur les comptes Swap-Free et Alpha Direct.',
          'Copie depuis un compte externe à votre nom admise avec preuve de propriété et après en avoir informé le support ; le trading de groupe est interdit.',
        ],
        sources: [
          'https://help.alphacapitalgroup.uk/en/articles/6934236-can-i-use-an-expert-advisor-ea',
          'https://alphacapitalgroup.uk/posts/copy-trading-uk-2026-how-it-works-rules-and-prop-firm-policies',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Best Day Rule 40% pour payout on-demand',
    },
    accounts: [
      { label: 'Alpha Pro 10% 50K', model: '2-step', sizeUsd: 50000, priceUsd: 297, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Alpha Pro 10% 100K', model: '2-step', sizeUsd: 100000, priceUsd: 497, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Alpha Pro 10% 200K', model: '2-step', sizeUsd: 200000, priceUsd: 997, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Alpha One 100K', model: '1-step', sizeUsd: 100000, priceUsd: 497, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: null },
      { label: 'Alpha Swing 100K', model: '2-step', sizeUsd: 100000, priceUsd: 577, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Alpha Three 100K', model: '3-step', sizeUsd: 100000, priceUsd: 397, targetP1Pct: 8, targetP2Pct: 4, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
    ],
    notes: "Alpha Pro décliné en variantes 6%/8%/10%. Max 200K$ par compte (400K$ cumulés). News OK sur Swing uniquement. Split plafonné à 80%.",
    sources: [
      'https://alphacapitalgroup.uk/resources/alpha-capital-review-2026-trader-verdict',
      'https://thetrustedprop.com/prop-firms/alpha-capital-group',
      'https://www.fxempire.com/prop-firms/alphacapitalgroup',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'E8 Markets',
    slug: 'e8-markets',
    category: 'forex',
    notorietyRank: 7,
    website: 'https://e8markets.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Dallas, TX, USA',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'Match-Trader', 'TradeLocker'],
    trustpilotScore: 4.3,
    trustpilotReviews: 3276,
    profitSplitMaxPct: 100,
    payoutFrequency: 'on-demand',
    maxAllocation: 500000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA admis tant que plusieurs utilisateurs ne jouent pas les mêmes trades : un EA partagé peut faire fermer le compte, E8 recommande un EA programmé par vous.',
          'HFT et arbitrage de latence interdits.',
          'Copie admise entre des comptes qui sont tous à vous ; signaux et comptes de tiers interdits.',
        ],
        sources: [
          'https://help.e8markets.com/en/articles/5515409-can-i-use-indicators-or-expert-advisors-when-trading-the-e8-account',
          'https://help.e8markets.com/en/articles/6929927-trading-policies-and-prohibited-trading-strategies',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Best Day 40% (One) / 35% (Signature) en funded',
    },
    accounts: [
      { label: 'E8 One 10K', model: '1-step', sizeUsd: 10000, priceUsd: 88, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'E8 One 25K', model: '1-step', sizeUsd: 25000, priceUsd: 188, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'E8 One 50K', model: '1-step', sizeUsd: 50000, priceUsd: 288, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'E8 One 100K', model: '1-step', sizeUsd: 100000, priceUsd: 488, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'E8 One 200K', model: '1-step', sizeUsd: 200000, priceUsd: 798, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'E8 Signature 100K', model: '1-step', sizeUsd: 100000, priceUsd: 260, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: null },
    ],
    notes: "E8 One personnalisable (cible 6-21%, DD 4-14%, valeurs par défaut listées). Signature: DD EOD 3-4% selon taille, pause à 2%/jour. Split jusqu'à 100% via add-on.",
    sources: [
      'https://thetrustedprop.com/prop-firms/e8-markets',
      'https://propfirmmap.com/firms/e8markets',
      'https://www.fxempire.com/prop-firms/e8markets',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Funded Trading Plus',
    slug: 'funded-trading-plus',
    category: 'forex',
    notorietyRank: 9,
    website: 'https://www.fundedtradingplus.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Londres, UK',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'DXtrade', 'Match-Trader'],
    trustpilotScore: 4.4,
    trustpilotReviews: 2653,
    profitSplitMaxPct: 100,
    payoutFrequency: 'hebdo (Express/Instant), 10j (Classic)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: 0,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA, algos et robots admis, y compris ceux du commerce, sur MT4, MT5 et cTrader ; pas sur DXtrade ni Match-Trader.',
          'Arbitrage, grid et tick scalping interdits.',
          'Copie admise entre vos propres comptes uniquement ; copier un autre trader, ou se laisser copier, annule les gains.',
        ],
        sources: [
          'https://help.fundedtradingplus.com/do-you-permit-copy-trading/',
          'https://www.eafunded.com/firms/funded-trading-plus',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: null,
    },
    accounts: [
      { label: '1-Step Express 10K', model: '1-step', sizeUsd: 10000, priceUsd: 99, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: false },
      { label: '1-Step Express 100K', model: '1-step', sizeUsd: 100000, priceUsd: 549, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: false },
      { label: '1-Step Express 200K', model: '1-step', sizeUsd: 200000, priceUsd: 999, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: false },
      { label: '2-Step Classic 10K', model: '2-step', sizeUsd: 10000, priceUsd: 89, targetP1Pct: 7, targetP2Pct: 7, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: false },
      { label: '2-Step Classic 100K', model: '2-step', sizeUsd: 100000, priceUsd: 549, targetP1Pct: 7, targetP2Pct: 7, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: false },
      { label: 'Instant Funding 100K', model: 'instant', sizeUsd: 100000, priceUsd: 4499, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 6, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Gamme simplifiée en 2026 (Instant / 1-Step Express / 2-Step Classic). 1er payout possible dès J+1. Scaling jusqu'à 2.5M$. Weekend interdit sur Instant.",
    sources: [
      'https://thetrustedprop.com/prop-firms/funded-trading-plus',
      'https://www.fxempire.com/prop-firms/fundedtradingplus',
      'https://www.trustpilot.com/review/fundedtradingplus.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'ThinkCapital',
    slug: 'thinkcapital',
    category: 'forex',
    notorietyRank: 10,
    website: 'https://thinkcapital.com',
    logoUrl: null,
    foundedYear: 2024,
    headquarters: 'Londres, UK',
    regulation: [],
    platforms: ['MT5', 'TradingView', 'autre'],
    trustpilotScore: 4,
    trustpilotReviews: 603,
    profitSplitMaxPct: 90,
    payoutFrequency: 'bi-mensuel',
    maxAllocation: 600000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'conditional',
        copyOwnAccounts: 'conditional',
        notes: [
          'EA admis sur MT5, en challenge comme en financé, après activation par le support ; le fichier de l’EA ou une preuve d’achat peuvent être demandés.',
          'Copie admise entre comptes ThinkCapital. Vers d’autres comptes, le compte ThinkCapital doit être le maître : copier depuis un compte personnel ou une autre propfirm est interdit.',
        ],
        sources: [
          'https://www.thinkcapital.com/faqs/general-faqs/are-expert-advisors-eas-allowed-at-thinkcapital/',
          'https://www.thinkcapital.com/tc-faqs/general-faqs/can-i-use-copy-trading-software-with-thinkcapital-accounts/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '3 jours profitables >=0.5% par cycle de payout',
    },
    accounts: [
      { label: 'Lightning 25K', model: '1-step', sizeUsd: 25000, priceUsd: 199, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'Lightning 100K', model: '1-step', sizeUsd: 100000, priceUsd: 499, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: 'Dual Step Intraday 100K', model: '2-step', sizeUsd: 100000, priceUsd: 499, targetP1Pct: 9, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 7, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Dual Step Swing 100K', model: '2-step', sizeUsd: 100000, priceUsd: 698, targetP1Pct: 9, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 7, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: 'Nexus 100K', model: '3-step', sizeUsd: 100000, priceUsd: 349, targetP1Pct: 7, targetP2Pct: 6, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
    ],
    notes: "Adossé au broker ThinkMarkets. Plateforme maison ThinkTrader. Fee remboursé après le 3e payout. Scaling 600K$ vers 1.5M$. Pas de taille >100K.",
    sources: [
      'https://thetrustedprop.com/prop-firms/thinkcapital',
      'https://propfirmkey.com/en/firms/thinkcapital',
      'https://www.trustpilot.com/review/thinkcapital.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Instant Funding',
    slug: 'instant-funding',
    category: 'forex',
    notorietyRank: 11,
    website: 'https://instantfunding.io',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'Royaume-Uni',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'Match-Trader'],
    trustpilotScore: null,
    trustpilotReviews: null,
    profitSplitMaxPct: 90,
    payoutFrequency: 'on-demand (challenges)',
    maxAllocation: 500000,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA, algorithmes et robots publics du commerce interdits ; un système propriétaire, à vous, est admis.',
          'La règle ne dit rien des EA de gestion : faire confirmer par le support avant de brancher le terminal.',
          'HFT interdit : tout trade tenu 60 secondes ou moins, ou un nombre anormal de trades par heure.',
          'Copie admise entre comptes du même titulaire, y compris depuis un compte à votre nom chez un courtier ou une autre propfirm.',
        ],
        sources: [
          'https://instantfunding.com/help/prohibited-trading-practice/',
          'https://instantfunding.com/help/is-copy-trading-allowed/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'risque max 1% par trade',
    },
    accounts: [
      { label: '1-Phase 5K', model: '1-step', sizeUsd: 5000, priceUsd: 44, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Phase 100K', model: '1-step', sizeUsd: 100000, priceUsd: 539, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Phase 200K', model: '1-step', sizeUsd: 200000, priceUsd: 1079, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Phase 5K', model: '2-step', sizeUsd: 5000, priceUsd: 48, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Phase 200K', model: '2-step', sizeUsd: 200000, priceUsd: 1078, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
    ],
    notes: "Trustpilot volatil en 2026 (purge d'avis: 3.1 en janv., ~4.5 en juin) donc non renseigné. Cibles 2-phase contestées (8/5 ou 10/5 selon sources). Comptes instant 625$-120K$. VPS/VPN et HFT interdits.",
    sources: [
      'https://thetrustedprop.com/prop-firms/instant-funding',
      'https://brokeranalysis.com/prop-trading-firms/instant-funding/',
      'https://www.trustpilot.com/review/instantfunding.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FTUK',
    slug: 'ftuk',
    category: 'forex',
    notorietyRank: 12,
    website: 'https://ftuk.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Royaume-Uni',
    regulation: [],
    platforms: ['MT5', 'Match-Trader', 'TradeLocker', 'autre'],
    trustpilotScore: 4,
    trustpilotReviews: 580,
    profitSplitMaxPct: 80,
    payoutFrequency: 'bi-mensuel (instant sur Instant Funding)',
    maxAllocation: null,
    rules: {
      minTradingDays: 4,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'unknown',
        notes: [
          'La firme n’annonce explicitement que les EA de gestion du risque ; martingale, grid, tick scalping, arbitrages et tout trade de moins de 30 secondes sont interdits, avec ou sans EA.',
          'Copier les trades d’un tiers est interdit ; la copie entre vos propres comptes n’est pas documentée.',
        ],
        sources: [
          'https://faq.ftuk.com/en/articles/14364398-is-it-possible-to-use-an-expert-advisor-ea-or-trading-bots',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: null,
    },
    accounts: [
      { label: '1-Step Accelerator 10K', model: '1-step', sizeUsd: 10000, priceUsd: 99, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: '1-Step Accelerator 100K', model: '1-step', sizeUsd: 100000, priceUsd: 649, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: '2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 649, targetP1Pct: 8, targetP2Pct: 6, maxDailyLossPct: 5, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: 'Instant Funding 5K', model: 'instant', sizeUsd: 5000, priceUsd: 119, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: 'Instant Funding 100K', model: 'instant', sizeUsd: 100000, priceUsd: 1499, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Split 50 vers 80% selon paliers. News trading via add-on payant. Scaling annoncé jusqu'à 6.4M$. Payout moyen traité en ~60 min. Plateforme maison FTUK XT (normalisée 'autre').",
    sources: [
      'https://www.dailyforex.com/prop-firms/ftuk-review',
      'https://tradersunion.com/brokers/prop/view/ftuk/',
      'https://bestpropfirmguide.com/firm/ftuk/',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Goat Funded Trader',
    slug: 'goat-funded-trader',
    category: 'forex',
    notorietyRank: 13,
    website: 'https://goatfundedtrader.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Las Palmas, Espagne',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'Match-Trader', 'TradeLocker'],
    trustpilotScore: null,
    trustpilotReviews: 4185,
    profitSplitMaxPct: 100,
    payoutFrequency: 'bi-mensuel (add-on à la demande)',
    maxAllocation: 2000000,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'conditional',
        notes: [
          'EA admis s’il est à vous : le code peut être demandé en preuve. Stratégie tierce, EA du commerce ou vendu pour passer les challenges = violation.',
          'HFT et EA d’arbitrage sur l’or interdits.',
          'Aucune duplication de trades vers un compte d’évaluation ni entre évaluations, à la main comme par EA ; la copie ne reste possible qu’entre vos comptes financés.',
        ],
        sources: [
          'https://help.goatfundedtrader.com/en/articles/10749630-can-i-use-expert-advisors-eas',
          'https://help.goatfundedtrader.com/en/articles/10742118-what-are-prohibited-trading-practices',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '15-20% selon modèle',
    },
    accounts: [
      { label: '2-Step GOAT 5K', model: '2-step', sizeUsd: 5000, priceUsd: 36, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step GOAT 10K', model: '2-step', sizeUsd: 10000, priceUsd: 66, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step GOAT 25K', model: '2-step', sizeUsd: 25000, priceUsd: 148, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step GOAT 50K', model: '2-step', sizeUsd: 50000, priceUsd: 248, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step GOAT 100K', model: '2-step', sizeUsd: 100000, priceUsd: 438, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: true },
    ],
    notes: "Profil Trustpilot signalé/masqué (2026, score ~2.6-3.4 selon captures). Fee remboursé au 1er payout. Promos quasi permanentes. Cible P2 : 4% ou 6% selon sources.",
    sources: [
      'https://goatfundedtrader.com/',
      'https://thetrustedprop.com/prop-firms/goat-funded-trader',
      'https://brokeranalysis.com/prop-trading-firms/goat-funded-trader/',
      'https://proptradingvibes.com/prop-firms/goat-funded-trader',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Maven Trading',
    slug: 'maven-trading',
    category: 'forex',
    notorietyRank: 14,
    website: 'https://maventrading.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'Royaume-Uni',
    regulation: [],
    platforms: ['MT5', 'Match-Trader', 'cTrader'],
    trustpilotScore: 4.6,
    trustpilotReviews: 5000,
    profitSplitMaxPct: 80,
    payoutFrequency: 'tous les 10 jours ouvrés',
    maxAllocation: 1000000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'forbidden',
        tradeManager: 'forbidden',
        copyOwnAccounts: 'forbidden',
        notes: [
          'Les EA sont interdits sur toutes les plateformes : en utiliser un empêche de valider le challenge, la récidive suspend le compte.',
          'Seule la copie manuelle entre vos propres comptes est tolérée.',
        ],
        sources: [
          'https://maventrading.com/day-trading-rules',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '20% (instant et comptes fundés)',
    },
    accounts: [
      { label: '2-Step 2K', model: '2-step', sizeUsd: 2000, priceUsd: 19, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 379, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 2K', model: '1-step', sizeUsd: 2000, priceUsd: 29, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 5, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 499, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 5, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: null },
      { label: '3-Step 100K', model: '3-step', sizeUsd: 100000, priceUsd: 299, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: 80, refundableFee: null },
    ],
    notes: "Prix promo très fréquents (entrée dès ~13$). Cibles 3-Step contradictoires selon sources (3% ou 5%/phase). Tailles 2K à 100K, scaling jusqu'à 1M$.",
    sources: [
      'https://maventrading.com/',
      'https://fx-brokers.co.uk/prop-firms/maven-trading',
      'https://lunefi.com/blog/maven-trading-complete-guide-to-rules-and-payouts',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'BrightFunded',
    slug: 'brightfunded',
    category: 'forex',
    notorietyRank: 15,
    website: 'https://brightfunded.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Dubaï, EAU',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'DXtrade'],
    trustpilotScore: null,
    trustpilotReviews: null,
    profitSplitMaxPct: 100,
    payoutFrequency: 'à la demande (1er payout ~14 j)',
    maxAllocation: 400000,
    priceCurrency: 'EUR',
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA admis ; l’API et le trading automatisé ne sont pas disponibles sur DXtrade.',
          'Copie admise entre tous les comptes du même titulaire — BrightFunded, autres propfirms, courtiers ; signaux de groupes et comptes de proches interdits.',
        ],
        sources: [
          'https://help.brightfunded.com/en/articles/9241699-can-i-use-ea',
          'https://help.brightfunded.com/en/articles/9241709-is-copy-trading-allowed',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'aucune (Prime/1-Step/Standard)',
    },
    accounts: [
      { label: '2-Step Classic 10K', model: '2-step', sizeUsd: 10000, priceUsd: 97, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Classic 25K', model: '2-step', sizeUsd: 25000, priceUsd: 197, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Classic 50K', model: '2-step', sizeUsd: 50000, priceUsd: 297, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Classic 100K', model: '2-step', sizeUsd: 100000, priceUsd: 497, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Bright 100K', model: '2-step', sizeUsd: 100000, priceUsd: 477, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 497, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Classic 200K', model: '2-step', sizeUsd: 200000, priceUsd: 997, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
    ],
    notes: "Prix officiels affichés en EUR (priceCurrency=EUR). Profil Trustpilot signalé (2026). Programme fidélité Trade2Earn. Payouts crypto uniquement.",
    sources: [
      'https://brightfunded.com/',
      'https://www.fxempire.com/prop-firms/brightfunded',
      'https://thetrustedprop.com/prop-firms/brightfunded',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Blueberry Funded',
    slug: 'blueberry-funded',
    category: 'forex',
    notorietyRank: 16,
    website: 'https://blueberryfunded.com',
    logoUrl: null,
    foundedYear: 2024,
    headquarters: 'Saint-Vincent-et-les-Grenadines',
    regulation: [],
    platforms: ['MT4', 'MT5', 'TradeLocker', 'DXtrade'],
    trustpilotScore: null,
    trustpilotReviews: 1501,
    profitSplitMaxPct: 90,
    payoutFrequency: 'bi-mensuel (14 j, add-on 7 j)',
    maxAllocation: 2000000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Un EA qui trade n’est admis que s’il est de vous — écrit par vous, sur votre stratégie — et déclaré au support avant usage. EA du commerce, commandé à un développeur, loué ou partagé : interdit.',
          'Les utilitaires qui assistent l’exécution ou la gestion de trades que vous décidez sont admis.',
          'Copie admise entre vos propres comptes, même sur des plateformes différentes ; signaux payants et gestion par un tiers interdits.',
        ],
        sources: [
          'https://help.blueberryfunded.com/en/articles/9550892-do-you-allow-bots-and-ea-expert-advisor-trading',
          'https://help.blueberryfunded.com/en/articles/9550602-am-i-allowed-to-copy-trade',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'aucune (Prime/1-Step/Standard)',
    },
    accounts: [
      { label: 'Prime 2-Step 10K', model: '2-step', sizeUsd: 10000, priceUsd: 90, targetP1Pct: 8, targetP2Pct: 6, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Prime 2-Step 50K', model: '2-step', sizeUsd: 50000, priceUsd: 325, targetP1Pct: 8, targetP2Pct: 6, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Prime 2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 650, targetP1Pct: 8, targetP2Pct: 6, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 550, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Standard 2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 590, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Instant Lite 50K', model: 'instant', sizeUsd: 50000, priceUsd: 420, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 4, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: null },
    ],
    notes: "Adossée au broker Blueberry Markets (ASIC). Note Trustpilot masquée par Trustpilot en 2026 (violation guidelines) ; ~4.3 auparavant. Cadre des stratégies interdites durci depuis mars 2026.",
    sources: [
      'https://blueberryfunded.com/',
      'https://thetrustedprop.com/prop-firms/blueberry-funded',
      'https://propfirmmatch.com/prop-firms/blueberry-funded',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'City Traders Imperium',
    slug: 'city-traders-imperium',
    category: 'forex',
    notorietyRank: 17,
    website: 'https://citytradersimperium.com',
    logoUrl: null,
    foundedYear: 2018,
    headquarters: null,
    regulation: [],
    platforms: ['MT5', 'Match-Trader'],
    trustpilotScore: 4.3,
    trustpilotReviews: 1697,
    profitSplitMaxPct: 100,
    payoutFrequency: null,
    maxAllocation: 4000000,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: null,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'Un EA développé par vous est admis, preuve de propriété à l’appui ; un EA acheté ou téléchargé ne l’est pas, et le partager avec d’autres traders est interdit.',
          'Copie admise entre vos comptes CTI et avec un compte externe à votre nom ; le compte source doit être le vôtre.',
          'Centre d’aide officiel non consulté : règles relevées sur des comparateurs publics, à confirmer auprès du support.',
        ],
        sources: [
          'https://tradingfinder.com/props/city-traders-imperium/rules/',
          'https://thetrustedprop.com/prop-firms/city-traders-imperium',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: null,
    },
    accounts: [
      { label: '1-Step 10K', model: '1-step', sizeUsd: 10000, priceUsd: 79, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 449, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: '2-Step 50K', model: '2-step', sizeUsd: 50000, priceUsd: 329, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 10, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: '2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 549, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 10, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Instant Funding 10K', model: 'instant', sizeUsd: 10000, priceUsd: 259, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Instant Funding 80K', model: 'instant', sizeUsd: 80000, priceUsd: 1599, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: null, refundableFee: null },
    ],
    notes: "Fee unique. HQ contradictoire selon sources (Londres vs EAU). Instant démarre à 50% du capital affiché. Daily loss 2-step = 50% de la cible P1. Règle anti one-sided betting appliquée manuellement.",
    sources: [
      'https://citytradersimperium.com/',
      'https://thetrustedprop.com/prop-firms/city-traders-imperium',
      'https://tradersunion.com/brokers/prop/view/city-traders-imperium/',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FXIFY',
    slug: 'fxify',
    category: 'forex',
    notorietyRank: 21,
    website: 'https://fxify.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Londres, Royaume-Uni',
    regulation: [],
    platforms: ['MT4', 'MT5', 'DXtrade', 'TradingView'],
    trustpilotScore: 4.4,
    trustpilotReviews: 5841,
    profitSplitMaxPct: 90,
    payoutFrequency: 'a la demande apres le 1er trade funde (bi-hebdo/mensuel selon programme)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'conditional',
        copyOwnAccounts: 'conditional',
        notes: [
          'EA admis sur MT4 et MT5 pour les programmes 1, 2 et 3 phases ; interdits sur DXtrade, sur Instant Funding et sur Lightning.',
          'Copie admise entre vos propres comptes ; depuis un compte externe, un relevé d’un mois du compte maître est à fournir. Interdite sur Instant Funding et Lightning.',
        ],
        sources: [
          'https://fxify.com/faqs/all-faqs/instant-funded-faq/can-i-use-expert-advisors-eas-or-bots-for-trading/',
          'https://www.fxempire.com/prop-firms/fxify',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '25-30% sur certains programmes (Lightning, 2-Step Classic funded, crypto) ; aucune sur 1-Step/2-Step Standard',
    },
    accounts: [
      { label: '2-Step Standard 5K', model: '2-step', sizeUsd: 5000, priceUsd: 59, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '2-Step Standard 400K', model: '2-step', sizeUsd: 400000, priceUsd: 2950, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: true },
      { label: '1-Step 5K', model: '1-step', sizeUsd: 5000, priceUsd: 59, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: null },
      { label: 'Lightning 100K (1-step, 7 jours)', model: '1-step', sizeUsd: 100000, priceUsd: 399, targetP1Pct: 5, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 4, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: 'Instant Funding 100K', model: 'instant', sizeUsd: 100000, priceUsd: 4249, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026). Broker-backed (FXPIG / Alchemy Markets). Split 80% de base, 90% via add-on payant (jusqu'a 100% sur certains plans crypto). Fee remboursee au 1er payout sur les evaluations standards. ~34-35M$ payes au total. Elu Best Broker Backed Prop Firm 2026. Traders US non acceptes.",
    sources: [
      'https://thetrustedprop.com/prop-firms/fxify',
      'https://fundedtrading.com/propfirm/fxify/',
      'https://www.fxempire.com/prop-firms/fxify',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'FunderPro',
    slug: 'funderpro',
    category: 'forex',
    notorietyRank: 22,
    website: 'https://funderpro.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Malte',
    regulation: [],
    platforms: ['MT5', 'cTrader', 'TradeLocker'],
    trustpilotScore: 3.9,
    trustpilotReviews: 1600,
    profitSplitMaxPct: 90,
    payoutFrequency: 'bi-hebdomadaire (Classic/One Phase), quotidien (Pro)',
    maxAllocation: 200000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'Un EA tiers n’est admis que si vous en possédez le code source ; location et partage interdits.',
          'La règle du code source vise tout EA tiers : faire confirmer par le support qu’un EA de gestion est admis.',
          'Copie admise entre des comptes qui sont tous à votre nom ; répliquer une stratégie par EA partagé ou service de signaux est interdit.',
        ],
        sources: [
          'https://support.funderpro.com/en/articles/527863-instant-trading-rule-violations-prohibited-behavior',
          'https://support.funderpro.com/en/articles/378022-is-copy-trading-allowed',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: null,
    },
    accounts: [
      { label: 'One Phase 5K', model: '1-step', sizeUsd: 5000, priceUsd: 69, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: null, drawdownType: null, profitSplitPct: 80, refundableFee: null },
      { label: 'Classic 2-Phase 100K', model: '2-step', sizeUsd: 100000, priceUsd: 539, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Classic 2-Phase 200K', model: '2-step', sizeUsd: 200000, priceUsd: 989, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Pro 2-Phase 5K', model: '2-step', sizeUsd: 5000, priceUsd: 75, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Pro 2-Phase 200K', model: '2-step', sizeUsd: 200000, priceUsd: 1319, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026), 21,5M$ payes en 2025. PRUDENCE : Trustpilot a appose un avertissement consommateur en janv. 2026 (patterns d'avis suspects dans les deux sens) ; plaintes recurrentes de refus de payout pour IP/VPS ou incoherence de lots. Split 80% base, 90% via add-on. Scaling annonce jusqu'a 5M$. Drawdown Classic base sur le solde quotidien (calcule a 17h EST).",
    sources: [
      'https://funderpro.com/',
      'https://thetrustedprop.com/prop-firms/funderpro',
      'https://bestfundedaccounts.com/reviews/funderpro/',
      'https://velotrade.com/blog/funderpro-review',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'TopTier Trader',
    slug: 'toptier-trader',
    category: 'forex',
    notorietyRank: 23,
    website: 'https://toptiertrader.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Etats-Unis',
    regulation: [],
    platforms: ['TradeLocker'],
    trustpilotScore: 4,
    trustpilotReviews: 4180,
    profitSplitMaxPct: 95,
    payoutFrequency: 'tous les 14 jours',
    maxAllocation: 300000,
    rules: {
      minTradingDays: 4,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'conditional',
        notes: [
          'Selon le programme : robots interdits sur le Challenge Regular, où tous les ordres doivent être passés à la main par le titulaire ; EA et copie ne sont ouverts que sur certains programmes.',
          'Copie interdite vers un Challenge Regular ; admise d’un Challenge Regular vers un Challenge Plus.',
          'Centre d’aide partiellement inaccessible lors de la vérification : à confirmer auprès du support.',
        ],
        sources: [
          'https://help.toptiertrader.com/en/collections/9507076-rules-for-accounts',
          'https://www.propfirmask.com/prop-firm/toptier-trader',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: "40% requis pour l'eligibilite au payout",
    },
    accounts: [
      { label: '2-Step 5K', model: '2-step', sizeUsd: 5000, priceUsd: 65, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Step 25K', model: '2-step', sizeUsd: 25000, priceUsd: 229, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Step 100K', model: '2-step', sizeUsd: 100000, priceUsd: 499, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Step 200K', model: '2-step', sizeUsd: 200000, priceUsd: 975, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Step 300K', model: '2-step', sizeUsd: 300000, priceUsd: 1345, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026) mais PRUDENCE : payouts en baisse de 78% au T1 2026 vs annee precedente — contraction severe de l'activite. Split jusqu'a 90%, 95% via scaling (jusqu'a 2M$). News trading via add-on. Daily loss calcule a 17h EST.",
    sources: [
      'https://thetrustedprop.com/prop-firms/toptier-trader',
      'https://tradersunion.com/brokers/prop/view/toptier-trader/',
      'https://www.financemagnates.com/forex/analysis/prop-firm-crypto-payouts-doubled-to-115-million-in-q1-2026-but-growth-has-stalled-since-december/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'The Trading Pit',
    slug: 'the-trading-pit',
    category: 'forex',
    notorietyRank: 24,
    website: 'https://www.thetradingpit.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Vaduz, Liechtenstein',
    regulation: [],
    platforms: ['MT4', 'MT5', 'Tradovate'],
    trustpilotScore: null,
    trustpilotReviews: null,
    profitSplitMaxPct: 80,
    payoutFrequency: 'tous les 14 jours (min 100$, 3 jours profitables requis)',
    maxAllocation: null,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Votre EA est admis tant qu’il ne copie pas de signaux qui ne sont pas les vôtres.',
          'Tenue minimale d’une minute sur les comptes CFD Classic ; HFT, arbitrages et émulateurs interdits.',
          'Aucun ordre ouvert ou en attente 2 minutes autour des annonces majeures sur les comptes CFD Prime 100 000 et 200 000 $.',
        ],
        sources: [
          'https://support.thetradingpit.com/can-i-use-an-expert-advisor-0',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'sur futures et stocks ; aucune sur CFD',
    },
    accounts: [
      { label: '1-Phase CFD 10K', model: '1-step', sizeUsd: 10000, priceUsd: 99, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '1-Phase CFD 50K', model: '1-step', sizeUsd: 50000, priceUsd: 349, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '1-Phase CFD 100K', model: '1-step', sizeUsd: 100000, priceUsd: 569, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Phase CFD 100K', model: '2-step', sizeUsd: 100000, priceUsd: 569, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Phase CFD 200K', model: '2-step', sizeUsd: 200000, priceUsd: 1139, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026) mais PRUDENCE : profil Trustpilot suspendu (juil. 2026, dernier score connu 3.6/934) ; placee sur liste noire par certains sites de review (ex. PropFirmCircle) pour application stricte/incoherente des regles. Multi-actifs (CFD, futures, stocks). Split CFD Prime 80% fixe ; Classic 60-70% evoluant jusqu'a 80%. Allocation max affichee 5M$ (marketing), ~250K$ selon les comparateurs. Drawdown des challenges CFD base sur le solde (balance-based).",
    sources: [
      'https://thetrustedprop.com/prop-firms/the-trading-pit',
      'https://propfirmmap.com/firms/thetradingpit',
      'https://tradersunion.com/brokers/prop/view/the-trading-pit/',
      'https://propfirmcircle.com/unlisted-prop-firms/the-trading-pit',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Blue Guardian',
    slug: 'blue-guardian',
    category: 'forex',
    notorietyRank: 25,
    website: 'https://blueguardian.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Dubai, EAU (Iconic Exchange FZCO)',
    regulation: [],
    platforms: ['MT4', 'MT5', 'Match-Trader', 'TradeLocker'],
    trustpilotScore: 3.6,
    trustpilotReviews: 2166,
    profitSplitMaxPct: 90,
    payoutFrequency: 'a la demande (garantie 24h)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA admis, réglés pour votre propre stratégie.',
          'Copie admise entre vos comptes Blue Guardian, entre évaluation et financé, et avec vos comptes externes, tous légalement à votre nom.',
          'VPN et VPS admis, sauf pour contourner les règles de copie, de groupe ou de signaux.',
        ],
        sources: [
          'https://help.blueguardian.com/en/articles/9661408-is-copy-trading-allowed',
          'https://help.blueguardian.com/en/articles/14062291-2-step-standard-rules',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '20% sur Instant et 2-Step Pro funded ; Guardian Shield coupe les trades a 1% (instant) / 2% (challenges) de perte ouverte',
    },
    accounts: [
      { label: '2-Step Standard 50K', model: '2-step', sizeUsd: 50000, priceUsd: 345, targetP1Pct: 8, targetP2Pct: 4, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: '2-Step Pro 50K', model: '2-step', sizeUsd: 50000, priceUsd: 250, targetP1Pct: 10, targetP2Pct: 4, maxDailyLossPct: 4, maxDrawdownPct: 10, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: '1-Step Standard 100K', model: '1-step', sizeUsd: 100000, priceUsd: 497, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
      { label: '3-Step 200K', model: '3-step', sizeUsd: 200000, priceUsd: 667, targetP1Pct: 6, targetP2Pct: 6, maxDailyLossPct: 4, maxDrawdownPct: 8, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
      { label: 'Instant Starter 5K', model: 'instant', sizeUsd: 5000, priceUsd: 27, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 5, drawdownType: 'trailing', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026), ~14M$ payes au total. Split 80-90% selon type de compte. EAs uniques seulement, copy trading entre ses propres comptes. PRUDENCE moderee : la regle Guardian Shield (fermeture auto a 1-2% de perte ouverte, 1er non-respect = split reduit a 50%) et la consistency sur Instant genent des traders profitables ; 18% d'avis 1 etoile.",
    sources: [
      'https://thetrustedprop.com/prop-firms/blue-guardian',
      'https://track360.io/blog/blue-guardian-prop-firm-review-2026-operator-watch',
      'https://mypropgenius.com/reviews/blue-guardian/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Audacity Capital',
    slug: 'audacity-capital',
    category: 'forex',
    notorietyRank: 26,
    website: 'https://audacity.capital',
    logoUrl: null,
    foundedYear: 2012,
    headquarters: 'Londres, Royaume-Uni',
    regulation: [],
    platforms: ['MT5', 'DXtrade'],
    trustpilotScore: 3.4,
    trustpilotReviews: 1067,
    profitSplitMaxPct: 95,
    payoutFrequency: 'hebdomadaire (Funded Trader Program) / tous les 14 jours (Ability Challenge)',
    maxAllocation: 240000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'Seul un EA développé par vous est admis : acheté ou loué, il est refusé.',
          'Rien n’est dit des EA de gestion tiers : faire confirmer par le support.',
          'Copie admise entre vos comptes Audacity et depuis un compte externe à votre nom, vérification du compte source possible.',
          'HFT et martingale interdits.',
        ],
        sources: [
          'https://audacitycapital.zendesk.com/hc/en-gb/articles/360018952339-Do-you-allow-EA-s',
          'https://audacity.capital/prohibited-trading-practices/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'score de consistance interne (>70 = auto-pass, 50-70 = revue manuelle)',
    },
    accounts: [
      { label: 'Ability One 1-Step 5K', model: '1-step', sizeUsd: 5000, priceUsd: 79, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Ability One 1-Step 120K', model: '1-step', sizeUsd: 120000, priceUsd: 949, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Ability Challenge 2-Step 240K', model: '2-step', sizeUsd: 240000, priceUsd: 1090, targetP1Pct: 10, targetP2Pct: 5, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Funded Trader Program (instant) 3K', model: 'instant', sizeUsd: 3000, priceUsd: 159, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: null, profitSplitPct: null, refundableFee: null },
      { label: 'Funded Trader Program (instant) 60K', model: 'instant', sizeUsd: 60000, priceUsd: 2399, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 10, drawdownType: null, profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026), plus ancienne firme du lot (2012, 14 ans). Split de depart bas (50-75%) montant a 95% via scaling (compte double apres 3 mois a +2.5%/mois). Ability Challenge : drawdowns par phase (P1 : 7.5% daily / 15% max ; P2 : 5% / 10%). Le warning FCA de 2025 vise un site CLONE (audacitycapital.trade), pas la firme elle-meme, qui publie une alerte anti-fraude. Plaintes recurrentes sur le calcul du daily drawdown trailing.",
    sources: [
      'https://thetrustedprop.com/prop-firms/audacity-capital',
      'https://www.fca.org.uk/news/warnings/audacity-capital-audacitycapitaltrade',
      'https://audacity.capital/scam-alert/',
      'https://audacity.capital/webinars/new-rules-better-trading/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'AquaFunded',
    slug: 'aqua-funded',
    category: 'forex',
    notorietyRank: 27,
    website: 'https://aquafunded.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Dubai, EAU (Dubai Silicon Oasis, AquaFunded FZCO)',
    regulation: [],
    platforms: ['Match-Trader', 'TradeLocker'],
    trustpilotScore: null,
    trustpilotReviews: null,
    profitSplitMaxPct: 100,
    payoutFrequency: 'bi-hebdomadaire (challenges) / a la demande sous 48h (instant)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: true,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA admis dans le cadre de votre propre stratégie.',
          'Copieurs admis pour gérer vos propres comptes AquaFunded, entre évaluation et financé, et avec un compte externe à votre nom.',
        ],
        sources: [
          'https://help.aquafunded.com/en/articles/11831659-are-eas-trade-copiers-allowed',
          'https://help.aquafunded.com/en/articles/11877755-is-copy-trading-allowed',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '15-25% selon programme (Instant 15-20%, 1-Step/2-Step Pro 25%, aucune sur 3-Step et Standard 2-Step)',
    },
    accounts: [
      { label: '1-Step Standard 5K', model: '1-step', sizeUsd: 5000, priceUsd: 67, targetP1Pct: 9, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: '1-Step Standard 200K', model: '1-step', sizeUsd: 200000, priceUsd: 1017, targetP1Pct: 9, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: '2-Step Standard 5K', model: '2-step', sizeUsd: 5000, priceUsd: 36, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: 'static', profitSplitPct: 90, refundableFee: null },
      { label: '2-Step Standard 200K', model: '2-step', sizeUsd: 200000, priceUsd: 947, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: 'static', profitSplitPct: 90, refundableFee: null },
      { label: 'Instant Standard 300K', model: 'instant', sizeUsd: 300000, priceUsd: 1810, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: null, drawdownType: 'static', profitSplitPct: null, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026), ~6,1M$ payes. PRUDENCE : Trustpilot a retire la note (breach of guidelines, faux avis detectes) — dernier score connu 3.0/1404. News : interdit +/-5 min autour des annonces high-impact, FOMC totalement interdit. 2-Step : DD 4-5% daily / 8-10% max selon la taille. Refund 150% + split 100% via promos/add-ons. Split standard 90%.",
    sources: [
      'https://thetrustedprop.com/prop-firms/aqua-funded',
      'https://tradersunion.com/reviews/aquafunded-com/',
      'https://www.directionsmag.com/reviews/prop-trading-firms/aqua-funded',
      'https://top30forexbrokers.com/product/aquafunded-review/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Funding Traders',
    slug: 'funding-traders',
    category: 'forex',
    notorietyRank: 28,
    website: 'https://fundingtraders.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'New York, Etats-Unis (Funding Traders LLC)',
    regulation: [],
    platforms: ['TradeLocker', 'DXtrade', 'MT5'],
    trustpilotScore: 4.5,
    trustpilotReviews: 2560,
    profitSplitMaxPct: 100,
    payoutFrequency: '1er payout a 14 jours, puis hebdomadaire (traitement 1-3 jours ouvres)',
    maxAllocation: 200000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: false,
      weekendHolding: false,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA de gestion du risque admis sans réserve ; un EA acheté ou téléchargé est interdit s’il fait autre chose.',
          'Un EA qui trade doit être compris de bout en bout par le trader : un EA généré par IA et recopié tel quel est traité comme un EA du commerce. EA de grid et de hedging interdits.',
          'Copie par logiciel admise entre vos comptes Funding Traders et depuis vos comptes chez d’autres courtiers ou firmes.',
          'HFT (trades de 15 secondes ou moins) interdit sur compte financé.',
        ],
        sources: [
          'https://fundingtraders.com/help/en/articles/12838492-general-trading-guidelines',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'jours profitables minimum requis sur fenetre de 30 jours ; risque flottant max 1% par position ; duree moyenne des trades > 2 min',
    },
    accounts: [
      { label: '1-Step 5K', model: '1-step', sizeUsd: 5000, priceUsd: 29, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: 80, refundableFee: true },
      { label: '1-Step 200K', model: '1-step', sizeUsd: 200000, priceUsd: 799, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: 80, refundableFee: true },
      { label: '2-Step 5K', model: '2-step', sizeUsd: 5000, priceUsd: 29, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 6, maxDrawdownPct: 12, drawdownType: null, profitSplitPct: 80, refundableFee: true },
      { label: '2-Step 200K', model: '2-step', sizeUsd: 200000, priceUsd: 799, targetP1Pct: 8, targetP2Pct: 5, maxDailyLossPct: 6, maxDrawdownPct: 12, drawdownType: null, profitSplitPct: 80, refundableFee: true },
      { label: 'Instant 100K', model: 'instant', sizeUsd: 100000, priceUsd: 589, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 3, maxDrawdownPct: 6, drawdownType: null, profitSplitPct: 80, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026), ~4,7M$ payes. PRUDENCE : avis tres polarises — TP 4.5 mais allegations serieuses de refus de paiement via les T&C. Regles strictes : EAs uniquement pre-approuves (gestion du risque), news restreint autour des annonces high-impact, positions fermees avant la cloture du vendredi, lot max 20, risque flottant 1%/position. Fee remboursee au 1er retrait. Split 80% base, 100% via add-on. Scaling jusqu'a 2M$.",
    sources: [
      'https://coinspot.io/en/prop-trading-firms/funding-traders/',
      'https://www.directionsmag.com/reviews/prop-trading-firms/funding-traders',
      'https://www.forexpeacearmy.com/forex-reviews/21201/fundingtraders-review',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'DNA Funded',
    slug: 'dna-funded',
    category: 'forex',
    notorietyRank: 29,
    website: 'https://dnafunded.com',
    logoUrl: null,
    foundedYear: 2024,
    headquarters: 'Sainte-Lucie (broker adosse : DNA Markets, Melbourne, regule ASIC)',
    regulation: [],
    platforms: ['TradeLocker'],
    trustpilotScore: 3.9,
    trustpilotReviews: null,
    profitSplitMaxPct: 90,
    payoutFrequency: 'mensuel (hebdomadaire via add-on), traitement 1-4 jours sans frais',
    maxAllocation: 600000,
    rules: {
      minTradingDays: 3,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: true,
      algo: {
        automated: 'conditional',
        tradeManager: 'conditional',
        copyOwnAccounts: 'allowed',
        notes: [
          'EA et algos admis sur les comptes 1 Phase, 2 Phases et Rapid ; interdits sur les comptes Instant Funding.',
          'Un EA tiers doit être approuvé avant usage sur un compte financé.',
          'HFT, arbitrage inverse et hedging interdits ; en financé s’ajoutent grid, martingale, tick scalping et scalping de news.',
          'Copie admise entre vos comptes DNA Funded ; depuis un compte externe ou entre utilisateurs différents, interdite.',
        ],
        sources: [
          'https://dnafunded.com/faq/trading-rules/',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: null,
    },
    accounts: [
      { label: 'Single Helix 1-Step 5K', model: '1-step', sizeUsd: 5000, priceUsd: 49, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: null, profitSplitPct: 80, refundableFee: false },
      { label: 'Single Helix 1-Step 200K', model: '1-step', sizeUsd: 200000, priceUsd: 1209, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: null, profitSplitPct: 80, refundableFee: false },
      { label: 'Double Helix 2-Step 5K', model: '2-step', sizeUsd: 5000, priceUsd: 49, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 6, maxDrawdownPct: 10, drawdownType: null, profitSplitPct: 80, refundableFee: false },
      { label: 'Rapid 10-Day 10K', model: '1-step', sizeUsd: 10000, priceUsd: 99, targetP1Pct: 5, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: 80, refundableFee: false },
      { label: 'Rapid 10-Day 100K', model: '1-step', sizeUsd: 100000, priceUsd: 549, targetP1Pct: 5, targetP2Pct: null, maxDailyLossPct: 4, maxDrawdownPct: 5, drawdownType: null, profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Operationnelle (juil. 2026). Lancee oct. 2024, adossee au broker DNA Markets (regule ASIC) — un des profils les plus permissifs : 800+ instruments, pas de restriction news/weekend/overnight, EAs et copy trading OK. Split 80%, 90% via add-on. Rapid Challenge limite a 10 jours. Plateforme TradeLocker uniquement, fee non remboursable, base d'avis Trustpilot encore reduite (jeune firme).",
    sources: [
      'https://www.dailyforex.com/prop-firms/dna-funded-review',
      'https://thetrustedprop.com/prop-firms/dna-funded',
      'https://tradingfinder.com/props/dna-funded/',
      'https://coincodecap.com/dna-funded',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Lark Funding',
    slug: 'lark-funding',
    category: 'forex',
    notorietyRank: 30,
    website: 'https://larkfunding.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'Montreal, Quebec, Canada',
    regulation: [],
    platforms: ['cTrader', 'DXtrade', 'Match-Trader'],
    trustpilotScore: 4.4,
    trustpilotReviews: 580,
    profitSplitMaxPct: 90,
    payoutFrequency: 'bi-hebdomadaire (1ere demande apres 14 jours)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: 0,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: false,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'conditional',
        notes: [
          'EA admis, hors HFT et arbitrage.',
          'Copie interdite entre évaluations : chacune se passe séparément, même à la main. Admise entre comptes financés, ou d’un compte financé vers une seule évaluation.',
        ],
        sources: [
          'https://larkfunding.com/faq/',
          'https://helpdesk.larkfunding.com/en/collections/3916920-trading-rules-guidelines',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: null,
    },
    accounts: [
      { label: '1-Step 5K', model: '1-step', sizeUsd: 5000, priceUsd: 75, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 500, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '1-Step 200K', model: '1-step', sizeUsd: 200000, priceUsd: 1000, targetP1Pct: 10, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 6, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: '3-Step 100K', model: '3-step', sizeUsd: 100000, priceUsd: 370, targetP1Pct: 5, targetP2Pct: 4, maxDailyLossPct: null, maxDrawdownPct: 5, drawdownType: 'static', profitSplitPct: 80, refundableFee: null },
      { label: 'Instant 10K', model: 'instant', sizeUsd: 10000, priceUsd: 400, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 5, maxDrawdownPct: 8, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
    ],
    notes: "Operationnelle (juil. 2026). Petite firme canadienne fondee le 1er juin 2022. Split 80-90% (instant : 90% fixe). Pas de MT4/MT5. 3-Step : cibles 5/4/3%, pas de limite journaliere. Weekend holding via add-on payant (+10%), copy trading interdit en evaluation, cap d'utilisation de marge 20%. Le programme 2-Step a ete supprime debut 2026 (comptes fundes fermes apres periode de grace). Volume de payouts modeste (~528K$ au total).",
    sources: [
      'https://thetrustedprop.com/prop-firms/lark-funding',
      'https://tradersunion.com/brokers/prop/view/lark-funding/',
      'https://propfirmmatch.com/blog/lark-funding-review-a-closer-look-at-the-platform',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Topstep',
    slug: 'topstep',
    category: 'futures',
    notorietyRank: 1,
    website: 'https://www.topstep.com',
    logoUrl: null,
    foundedYear: 2012,
    headquarters: 'Chicago, USA',
    regulation: [],
    platforms: ['autre', 'NinjaTrader', 'Tradovate'],
    trustpilotScore: 3.4,
    trustpilotReviews: 13721,
    profitSplitMaxPct: 90,
    payoutFrequency: 'par cycle de jours gagnants (caps par taille)',
    maxAllocation: null,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Stratégies automatisées et robots admis via l’API TopstepX / ProjectX, HFT exclu ; Topstep n’assiste ni ne couvre les erreurs d’un robot.',
          'Tout le trading doit partir de votre propre machine : VPS, VPN et serveurs distants sont interdits.',
          'Un copieur peut répliquer vos trades entre vos propres comptes.',
        ],
        sources: [
          'https://help.topstep.com/en/articles/11187768-topstepx-api-access',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: 'oui (objectif Combine + chemin payout XFA)',
    },
    accounts: [
      { label: 'Trading Combine 50K ($49/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 49, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Trading Combine 100K ($99/mois)', model: '1-step', sizeUsd: 100000, priceUsd: 99, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Trading Combine 150K ($149/mois)', model: '1-step', sizeUsd: 150000, priceUsd: 149, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
    ],
    notes: "Futures CME. Abonnement mensuel + activation $149. TopstepX obligatoire pour nouveaux comptes (NinjaTrader/Tradovate legacy). Split 90/10 dès le 1er dollar depuis 01/2026. Cibles/pertes en $ : ex. 50K = cible $3 000, MLL $2 000. Daily loss obligatoire supprimé depuis 08/2024 (DLL personnel optionnel).",
    sources: [
      'https://www.topstep.com/',
      'https://tradecovex.com/guides/topstep-combine-account-sizes-profit-targets-2026',
      'https://tradecovex.com/guides/topstep-payout-rules-2026',
      'https://proptradingvibes.com/prop-firms/topstep',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Apex Trader Funding',
    slug: 'apex-trader-funding',
    category: 'futures',
    notorietyRank: 2,
    website: 'https://apextraderfunding.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Austin, Texas, USA',
    regulation: [],
    platforms: ['Rithmic', 'Tradovate', 'NinjaTrader', 'TradingView'],
    trustpilotScore: 4.4,
    trustpilotReviews: 17860,
    profitSplitMaxPct: 100,
    payoutFrequency: 'tous les 5 jours de trading (caps par taille)',
    maxAllocation: null,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'forbidden',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'IA, robots, algorithmes, systèmes entièrement automatisés et HFT sont interdits sur les comptes PA et Live.',
          'Un copieur est admis entre vos propres comptes (jusqu’à 20 comptes PA), à condition que le compte maître soit tradé à la main ; copier un tiers ou partager ses trades est interdit.',
        ],
        sources: [
          'https://apextraderfunding.com/help-center/getting-started/prohibited-activities/',
          'https://support.apextraderfunding.com/hc/en-us/articles/31519788944411-Performance-Account-PA-and-Compliance',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: null,
    },
    accounts: [
      { label: 'Eval Intraday 25K', model: '1-step', sizeUsd: 25000, priceUsd: 199, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 6, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: 'Eval Intraday 50K', model: '1-step', sizeUsd: 50000, priceUsd: 249, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 5, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: 'Eval Intraday 100K', model: '1-step', sizeUsd: 100000, priceUsd: 399, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: 'Eval Intraday 150K', model: '1-step', sizeUsd: 150000, priceUsd: 599, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.33, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: null },
      { label: 'Eval EOD 100K', model: '1-step', sizeUsd: 100000, priceUsd: 590, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: null },
    ],
    notes: "Apex 4.0 (03/2026) : fee unique (fin des abonnements), activation PA $79-$99, tailles 250K/300K supprimées, flat 16h59 ET (pas d'overnight). 100% des premiers $25K de profits puis 90/10. Promos 50-80% quasi permanentes. Drawdown en $ : 25K=$1 500, 50K=$2 500, 100K=$3 000, 150K=$5 000.",
    sources: [
      'https://apextraderfunding.com/',
      'https://phidiaspropfirm.com/education/apex-cost',
      'https://quantcrawler.com/learn/apex-trader-funding-rules',
      'https://blog.traderspost.io/article/apex-trader-funding-pricing-evaluation-guide',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'MyFundedFutures',
    slug: 'myfundedfutures',
    category: 'futures',
    notorietyRank: 3,
    website: 'https://myfundedfutures.com',
    logoUrl: null,
    foundedYear: 2023,
    headquarters: 'Texas, USA',
    regulation: [],
    platforms: ['Tradovate', 'NinjaTrader', 'Rithmic', 'autre'],
    trustpilotScore: 4.9,
    trustpilotReviews: 19900,
    profitSplitMaxPct: 90,
    payoutFrequency: 'quotidien (Rapid) / 14 j (Pro) / 48 h (Builder)',
    maxAllocation: null,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'conditional',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Stratégies automatisées admises si elles sont réglées par vous et n’exploitent pas les exécutions favorables de la simulation ; HFT interdit.',
          'Sur compte live, l’automatisation suit les règles du CME.',
          'Copie entre traders interdite ; un copieur entre vos propres comptes est admis.',
        ],
        sources: [
          'https://help.myfundedfutures.com/en/articles/8444599-fair-play-and-prohibited-trading-practices',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'aucune (Rapid/Pro) ; 50% Builder (sim-funded)',
    },
    accounts: [
      { label: 'Builder 50K ($77/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 77, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Rapid 50K ($157/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 157, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Rapid 100K ($229/mois)', model: '1-step', sizeUsd: 100000, priceUsd: 229, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Pro 50K ($227/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 227, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Pro 100K ($329/mois)', model: '1-step', sizeUsd: 100000, priceUsd: 329, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Éval 1 phase, aucun daily loss limit. Rapid = drawdown intraday trailing, payouts quotidiens, split 90/10 ; Pro/Builder = EOD trailing, 80/20. Options one-time dispo (ex. Rapid 100K ~$267). News T1 : flat 2 min avant/après FOMC/CPI/NFP selon plan.",
    sources: [
      'https://myfundedfutures.com/',
      'https://help.myfundedfutures.com/en/articles/13286582-rapid-plan-150k-a-comprehensive-look',
      'https://proptradingvibes.com/blog/myfundedfutures-account-types',
      'https://propscope.net/en/myfundedfutures/',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Earn2Trade',
    slug: 'earn2trade',
    category: 'futures',
    notorietyRank: 4,
    website: 'https://www.earn2trade.com',
    logoUrl: null,
    foundedYear: null,
    headquarters: null,
    regulation: [],
    platforms: ['NinjaTrader', 'Rithmic', 'autre'],
    trustpilotScore: 4.7,
    trustpilotReviews: 4693,
    profitSplitMaxPct: 80,
    payoutFrequency: 'hebdomadaire (demande vendredi, paiement mercredi)',
    maxAllocation: 400000,
    rules: {
      minTradingDays: 0,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'forbidden',
        tradeManager: 'unknown',
        copyOwnAccounts: 'unknown',
        notes: [
          'Tout logiciel, IA ou saisie ultra-rapide susceptible de fausser l’environnement de trading est interdit, en évaluation comme en LiveSim et en réel ; les relevés publics concordent sur l’interdiction des stratégies automatisées.',
          'Aucune règle publiée sur les copieurs entre vos propres comptes : à confirmer auprès du support.',
        ],
        sources: [
          'https://help.earn2trade.com/en/articles/9286647-prohibited-conduct-in-earn2trade-evaluations-and-in-the-livesim-live-trading-environment',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '30% max/jour (éval uniquement)',
    },
    accounts: [
      { label: 'Gauntlet Mini 50K ($170/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 170, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.2, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'TCP 25K ($150/mois)', model: '1-step', sizeUsd: 25000, priceUsd: 150, targetP1Pct: 7, targetP2Pct: null, maxDailyLossPct: 2.2, maxDrawdownPct: 6, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'TCP 50K ($250/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 250, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.2, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'TCP 100K ($350/mois)', model: '1-step', sizeUsd: 100000, priceUsd: 350, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.2, maxDrawdownPct: 3.5, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Futures CME uniquement. Abonnement mensuel, promos ~60% fréquentes. Année de création contradictoire selon sources (2016/2017). Activation LiveSim $139. Plateforme maison Finamark. TCP scale jusqu'à $400K.",
    sources: [
      'https://www.earn2trade.com/',
      'https://propnavi.io/en/blog/earn2trade-review/',
      'https://propscope.net/en/earn2trade/',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'TradeDay',
    slug: 'tradeday',
    category: 'futures',
    notorietyRank: 5,
    website: 'https://www.tradeday.com',
    logoUrl: null,
    foundedYear: 2020,
    headquarters: 'Chicago, USA',
    regulation: [],
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'autre'],
    trustpilotScore: 4.6,
    trustpilotReviews: 1360,
    profitSplitMaxPct: null,
    payoutFrequency: 'dès le jour 1 (Quick Pay, min $250)',
    maxAllocation: null,
    rules: {
      minTradingDays: 5,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'conditional',
        notes: [
          'Algos et robots achetés à un tiers interdits ; une stratégie à vous, exécutée depuis une plateforme supportée (NinjaTrader, Tradovate, TradingView, Jigsaw, Quantower), est admise. L’API Tradovate n’est pas ouverte.',
          'Plus de 200 trades par jour ou scalping à très haute fréquence : interdit. Plusieurs utilisateurs faisant les mêmes trades = comptes fermés.',
          'Copieur admis entre vos propres comptes ; un compte Funded Live ne se copie pas avec un Funded Sim.',
        ],
        sources: [
          'https://tradeday.freshdesk.com/en/support/solutions/articles/103000085101-automated-algo-and-bot-trading',
          'https://tradeday.freshdesk.com/en/support/solutions/articles/103000121031-prohibited-trade-practices',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: '30% Quick Pay / 45% Fast Pass (éval uniquement)',
    },
    accounts: [
      { label: 'Quick Pay Intraday 50K ($125/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 125, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: null, profitSplitPct: null, refundableFee: false },
      { label: 'Quick Pay EOD 50K ($175/mois)', model: '1-step', sizeUsd: 50000, priceUsd: 175, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: null, refundableFee: false },
      { label: 'Quick Pay Intraday 100K ($230/mois)', model: '1-step', sizeUsd: 100000, priceUsd: 230, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: null, profitSplitPct: null, refundableFee: false },
      { label: 'Quick Pay Intraday 150K ($350/mois)', model: '1-step', sizeUsd: 150000, priceUsd: 350, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: null, profitSplitPct: null, refundableFee: false },
    ],
    notes: "TradeDay 2.0 : tailles réduites à 50/100/150K. Split Quick Pay : 50/50 sous $4k bruts puis 80/20 (paliers historiques jusqu'à 95% selon sources — non confirmé). Intraday only, pas d'overnight. Jusqu'à 6 comptes fundés. Promos 50% fréquentes.",
    sources: [
      'https://www.tradeday.com/',
      'https://proptradingauthority.com/reviews/tradeday-prop-firm-review/',
      'https://blog.traderspost.io/article/tradeday-review',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Take Profit Trader',
    slug: 'take-profit-trader',
    category: 'futures',
    notorietyRank: 6,
    website: 'https://takeprofittrader.com',
    logoUrl: null,
    foundedYear: 2021,
    headquarters: 'Windermere, Floride, USA',
    regulation: [],
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'autre'],
    trustpilotScore: 4.5,
    trustpilotReviews: 9979,
    profitSplitMaxPct: 90,
    payoutFrequency: 'Quotidien (retraits possibles dès le jour 1 en compte PRO)',
    maxAllocation: 750000,
    rules: {
      minTradingDays: 5,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'forbidden',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Les conditions générales cherchent des « traders discrétionnaires, pas des robots » : robots autonomes et algorithmique à très court terme font échouer le compte.',
          'Copieurs et outils d’automatisation tiers tolérés sous votre seule responsabilité ; copier vos propres trades entre vos comptes est admis.',
          'Conditions générales non consultées directement : citations relevées chez deux intégrateurs, à confirmer auprès du support.',
        ],
        sources: [
          'https://blog.traderspost.io/article/automate-take-profit-trader-with-traderspost',
          'https://crosstrade.io/prop-firms/take-profit-trader',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Test : aucun jour > 50% du profit total',
    },
    accounts: [
      { label: 'Test 25K', model: '1-step', sizeUsd: 25000, priceUsd: 150, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 6, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Test 50K', model: '1-step', sizeUsd: 50000, priceUsd: 170, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Test 75K', model: '1-step', sizeUsd: 75000, priceUsd: 245, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.33, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Test 100K', model: '1-step', sizeUsd: 100000, priceUsd: 330, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Test 150K', model: '1-step', sizeUsd: 150000, priceUsd: 360, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026. Abonnement mensuel, eval 1-step avec drawdown EOD trailing ; ATTENTION : une fois finance (compte PRO), le drawdown devient trailing INTRADAY, plus strict. Frais d'activation PRO 130 $ (souvent offerts en promo). Split 80/20 en PRO, 90/10 en PRO+ (live, sur invitation). Pas de daily loss limit. Day-trading uniquement (flat en fin de seance, pas de weekend). Quelques plaintes de traders sur des changements de regles, mais firme etablie (~10k avis Trustpilot) et payouts quotidiens effectifs. Plateformes Quantower/MotiveWave/R|Trader normalisees 'autre'.",
    sources: [
      'https://www.propscorer.com/firms/take-profit-trader',
      'https://blog.traderspost.io/article/take-profit-trader-pricing-evaluation-guide',
      'https://takeprofittrader.com/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Tradeify',
    slug: 'tradeify',
    category: 'futures',
    notorietyRank: 7,
    website: 'https://tradeify.co',
    logoUrl: null,
    foundedYear: 2024,
    headquarters: 'Boca Raton, Floride, USA',
    regulation: [],
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Rithmic', 'autre'],
    trustpilotScore: 4.6,
    trustpilotReviews: 3600,
    profitSplitMaxPct: 90,
    payoutFrequency: 'Quotidien (voie Select Daily) ou cycle 5 jours ; traitement 7j/7 via Rise',
    maxAllocation: 750000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'allowed',
        notes: [
          'Robots et algorithmes admis si vous en êtes le seul propriétaire et utilisateur, et s’ils ne tournent que chez Tradeify ; une vidéo de l’activation du code sur votre PC peut être exigée. HFT exclu.',
          'Copie admise entre les comptes Tradeify que vous possédez et gérez ; copier un autre trader est interdit.',
        ],
        sources: [
          'https://help.tradeify.co/en/articles/10468318-guidelines-for-traders',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Growth : aucune en eval, 35% une fois finance ; Select : 40% en eval, aucune finance ; Lightning : progressive 20% puis 25% puis 30%',
    },
    accounts: [
      { label: 'Growth 25K', model: '1-step', sizeUsd: 25000, priceUsd: 99, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.4, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Growth 50K', model: '1-step', sizeUsd: 50000, priceUsd: 149, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.4, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Growth 100K', model: '1-step', sizeUsd: 100000, priceUsd: 249, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Growth 150K', model: '1-step', sizeUsd: 150000, priceUsd: 349, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Select 25K', model: '1-step', sizeUsd: 25000, priceUsd: 109, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Lightning 50K (instant)', model: 'instant', sizeUsd: 50000, priceUsd: 499, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026, forte croissance (100 M$ de payouts annonces). Depuis avril 2026 : pricing one-time uniquement (plus d'abonnement), aucun frais d'activation. 3 gammes : Growth (DLL souple, pas de consistency en eval), Select (pas de DLL en eval), Lightning (finance instantane). Drawdown EOD trailing sur toutes les gammes. Split 100% sur les premiers 15 000 $ puis 90/10. Max 5 comptes / 750K$. Annee de creation confirmee 2024 (juin, CEO Brett Simberkoff). Plateformes Quantower/TradeSea/WealthCharts normalisees 'autre'.",
    sources: [
      'https://proptradingvibes.com/prop-firms/tradeify',
      'https://damnpropfirms.com/futures-prop-firms/tradeify/',
      'https://tradeify.co/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Elite Trader Funding',
    slug: 'elite-trader-funding',
    category: 'futures',
    notorietyRank: 8,
    website: 'https://elitetraderfunding.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'USA (societe Delaware)',
    regulation: [],
    platforms: ['Rithmic', 'Tradovate', 'NinjaTrader'],
    trustpilotScore: 3.8,
    trustpilotReviews: 1079,
    profitSplitMaxPct: 90,
    payoutFrequency: 'Bi-hebdomadaire (lundi et mercredi) en sim-funded ; quotidien sur la voie live',
    maxAllocation: 1300000,
    rules: {
      minTradingDays: 5,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'conditional',
        notes: [
          'IA, robots, EA et systèmes automatisés interdits sauf autorisation écrite d’ETF.',
          'Copieurs : seuls les partenaires approuvés (Tradesyncer, Tradecopia, Affordable Indicators, Replikanto édition conformité) et la fonction de groupe de Tradovate sont admis ; tout autre copieur expose à la remise à zéro, puis au bannissement.',
        ],
        sources: [
          'https://www.elitetraderfunding.app/help/trade-copier-disclaimer',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Aucune en eval standard (regle 40% supprimee en 2024) ; 40% sur Fast Track ; regle ATD (jour actif = 200 $ et 23% du meilleur jour) pour les cycles de payout',
    },
    accounts: [
      { label: '1-Step 50K', model: '1-step', sizeUsd: 50000, priceUsd: 197, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'trailing', profitSplitPct: null, refundableFee: false },
      { label: '1-Step 100K', model: '1-step', sizeUsd: 100000, priceUsd: 247, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3, drawdownType: 'trailing', profitSplitPct: null, refundableFee: false },
      { label: '1-Step 150K', model: '1-step', sizeUsd: 150000, priceUsd: 347, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.33, drawdownType: 'trailing', profitSplitPct: null, refundableFee: false },
      { label: 'End of Day 100K', model: '1-step', sizeUsd: 100000, priceUsd: 487, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2.2, maxDrawdownPct: 3.5, drawdownType: 'eod-trailing', profitSplitPct: null, refundableFee: false },
      { label: 'Static 50K', model: '1-step', sizeUsd: 50000, priceUsd: 497, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'static', profitSplitPct: null, refundableFee: false },
      { label: 'Direct to Funded 50K (EOD)', model: 'instant', sizeUsd: 50000, priceUsd: 747, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 10, drawdownType: 'eod-trailing', profitSplitPct: null, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026 (revue au 16/07/2026), active depuis 2022, 10 M$+ de payouts. 6 modeles d'eval (1-Step, End of Day, Static, Diamond Hands, Fast Track, Direct to Funded) ; le 1-Step utilise un drawdown trailing INTRADAY (live), l'eval EOD un drawdown EOD. Les prix d'eval affiches sont des abonnements MENSUELS recurrents (197 $/mois 50K 1-Step, 247 $/mois 100K, 347 $/mois 150K). Split : 100% des premiers 12 500 $ en sim puis 90/10, cap sim 25 000 $ avant passage sur la voie live (80/20, payouts quotidiens). Activation sim-funded : 87 $/mois ou 177-307 $ one-time. Commissions facturees (~2 $/RT mini, 0,62 $/RT micro). Prudence relative : Trustpilot le plus faible du panel (3,8) avec avis mitiges, mais firme active et payante.",
    sources: [
      'https://propfirmapp.com/prop-firms/elite-trader-funding',
      'https://www.propscorer.com/firms/elite-trader-funding',
      'https://reviews.bestpropfirms.com/reviews/elite-trader-funding/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Alpha Futures',
    slug: 'alpha-futures',
    category: 'futures',
    notorietyRank: 9,
    website: 'https://alpha-futures.com',
    logoUrl: null,
    foundedYear: 2024,
    headquarters: 'Londres, Royaume-Uni',
    regulation: [],
    platforms: ['autre'],
    trustpilotScore: 4.9,
    trustpilotReviews: 5613,
    profitSplitMaxPct: 90,
    payoutFrequency: 'A la demande, traitement le jour meme a 48h max',
    maxAllocation: 750000,
    rules: {
      minTradingDays: null,
      timeLimitDays: null,
      newsTrading: true,
      weekendHolding: null,
      algo: {
        automated: 'forbidden',
        tradeManager: 'conditional',
        copyOwnAccounts: 'allowed',
        notes: [
          'IA, robots et toute automatisation complète sont interdits sur tous les comptes.',
          'Le semi-automatique est admis — un indicateur qui donne des signaux — si vous placez, surveillez et gérez chaque trade vous-même.',
          'Plus de 100 trades par jour : refusé, automatisé ou non.',
          'Copie admise entre les comptes d’un même titulaire ; le trading de groupe est interdit.',
        ],
        sources: [
          'https://help.alpha-futures.com/en/articles/9508585-prohibited-trading-practices',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: '40% en compte qualifie Zero ; aucune en Advanced qualifie ; 20% sur Direct',
    },
    accounts: [
      { label: 'Zero 50K', model: '1-step', sizeUsd: 50000, priceUsd: 119, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Zero 100K', model: '1-step', sizeUsd: 100000, priceUsd: 239, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: 2, maxDrawdownPct: 3, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Advanced 50K', model: '1-step', sizeUsd: 50000, priceUsd: 139, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.5, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Advanced 100K', model: '1-step', sizeUsd: 100000, priceUsd: 279, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.5, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Advanced 150K', model: '1-step', sizeUsd: 150000, priceUsd: 419, targetP1Pct: 8, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.5, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Direct 50K (instant)', model: 'instant', sizeUsd: 50000, priceUsd: 519, targetP1Pct: null, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: null, drawdownType: null, profitSplitPct: 90, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026, adossee a Alpha Capital Group (UK). Eval 1-step, drawdown EOD base sur le solde, payouts rapides. Frais d'activation : Zero 0 $, Advanced 149 $ (seul Zero est sans frais). Zero/Advanced en abonnement mensuel, Direct en one-time (qualifie immediatement). Transition importante : rupture avec NinjaTrader/Tradovate le 12/07/2026 (plan Premium supprime) -> plateformes AlphaTrader/WealthCharts/Quantower/Deepcharts (toutes normalisees 'autre'). Min jours de trading : 1 a 5 selon plan. Caps de retrait par cycle sur Zero et Direct (env. 1-3k$), 15k$ sur Advanced. Trustpilot excellent (4,9) malgre quelques avis isoles mettant en doute la capacite de paiement.",
    sources: [
      'https://alpha-futures.com/',
      'https://www.propscorer.com/firms/alpha-futures',
      'https://propfirmmap.com/firms/alphafutures',
      'https://propscope.net/en/alpha-futures/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'BluSky Trading',
    slug: 'blusky-trading',
    category: 'futures',
    notorietyRank: 10,
    website: 'https://blusky.pro',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'St. Petersburg, Floride, USA',
    regulation: [],
    platforms: ['NinjaTrader', 'Tradovate', 'Rithmic', 'autre'],
    trustpilotScore: 4.8,
    trustpilotReviews: 833,
    profitSplitMaxPct: 90,
    payoutFrequency: 'Quotidien en semaine (meme jour si demande avant 11h ET), minimum 250 $',
    maxAllocation: null,
    rules: {
      minTradingDays: 8,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: false,
      algo: {
        automated: 'allowed',
        tradeManager: 'allowed',
        copyOwnAccounts: 'allowed',
        notes: [
          'Stratégies algorithmiques admises en évaluation comme en financé ; sont exclus les systèmes qui saturent la bourse de messages ou exploitent les latences.',
          'Copieur admis sur tous les types de compte, entre vos propres comptes ; copier un autre trader ou un fournisseur de signaux est interdit.',
          'Trois comptes financés au maximum.',
        ],
        sources: [
          'https://help.blusky.pro/en/articles/12433943-multiple-accounts-copy-trading',
          'https://help.blusky.pro/en/articles/12069208-ethical-trading-conduct-policy-blusky-trading-company',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: true,
      consistencyRule: '30% en eval (21% sur les plus gros comptes, ~50% sur Launch)',
    },
    accounts: [
      { label: 'Launch 50K', model: '1-step', sizeUsd: 50000, priceUsd: 59, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Launch+ 100K', model: '1-step', sizeUsd: 100000, priceUsd: 69, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 2.5, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Premium+ 100K', model: '1-step', sizeUsd: 100000, priceUsd: 147, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 2.5, drawdownType: 'eod-trailing', profitSplitPct: 90, refundableFee: false },
      { label: 'Static Growth+ 200K', model: '1-step', sizeUsd: 200000, priceUsd: 154, targetP1Pct: 3, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 1, drawdownType: 'static', profitSplitPct: 90, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026, 10 M$+ de payouts. Particularite : parcours de financement en 4 etapes (Eval -> BluLive -> Sim Funded -> compte live de brokerage reel via Rithmic), le plus proche d'un vrai passage en capital live du secteur. Abonnements mensuels ; frais d'activation ~99 $ sur les plans Launch uniquement. Min 8 jours de trading en eval, split 90/10 des le depart. Caps de payout glissants 7 jours (1 500-3 000 $/compte). News trading autorise en eval mais interdit une fois finance ; pas de positions le weekend. Prix tres changeants (promos frequentes) — a verifier avant achat. Trustpilot 4,7-4,8 selon les instantanes (~830 avis). Plateformes Volumetrica/TradeSea/Tickblaze normalisees 'autre'.",
    sources: [
      'https://damnpropfirms.com/futures-prop-firms/blusky/',
      'https://www.propscorer.com/firms/blusky',
      'https://coincodecap.com/blusky-review',
      'https://blusky.pro/',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Funded Futures Network',
    slug: 'funded-futures-network',
    category: 'futures',
    notorietyRank: 11,
    website: 'https://fundedfuturesnetwork.com',
    logoUrl: null,
    foundedYear: 2022,
    headquarters: 'USA',
    regulation: [],
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView'],
    trustpilotScore: 4.2,
    trustpilotReviews: 461,
    profitSplitMaxPct: 90,
    payoutFrequency: 'Meme jour / sous 24h une fois eligible (minimum ~500 $)',
    maxAllocation: null,
    rules: {
      minTradingDays: 7,
      timeLimitDays: null,
      newsTrading: null,
      weekendHolding: null,
      algo: {
        automated: 'conditional',
        tradeManager: 'unknown',
        copyOwnAccounts: 'unknown',
        notes: [
          'Le trading automatisé est admis « à la discrétion de FFN » ; robots HFT et exploitation de la simulation font perdre le compte.',
          'Règles de copie entre vos comptes non publiées de façon exploitable, et FAQ officielle inaccessible lors de la vérification : à confirmer auprès du support.',
        ],
        sources: [
          'https://fundedfuturesnetwork.zendesk.com/hc/en-us/articles/47493608444059-FFN-Frequently-Asked-Questions',
        ],
        verifiedAt: '2026-09-17',
      },
      scalingPlan: null,
      consistencyRule: 'Meilleur jour <= 40% du profit total (Standard) ; <= 25% (Express)',
    },
    accounts: [
      { label: 'Standard 50K', model: '1-step', sizeUsd: 50000, priceUsd: 150, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Standard 100K', model: '1-step', sizeUsd: 100000, priceUsd: 305, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.6, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Standard 150K', model: '1-step', sizeUsd: 150000, priceUsd: 350, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 3.33, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Standard 250K', model: '1-step', sizeUsd: 250000, priceUsd: 580, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 2.4, drawdownType: 'eod-trailing', profitSplitPct: 80, refundableFee: false },
      { label: 'Express 50K', model: '1-step', sizeUsd: 50000, priceUsd: 175, targetP1Pct: 6, targetP2Pct: null, maxDailyLossPct: null, maxDrawdownPct: 4, drawdownType: 'trailing', profitSplitPct: 80, refundableFee: false },
    ],
    notes: "Operationnelle juillet 2026. Petite firme (fondee 2022 par Kevin Swart et Jay, ~460 avis Trustpilot) reputee pour la rapidite des payouts (meme jour) et un onboarding call live obligatoire avant de trader. Abonnement mensuel, eval 1-step, drawdown EOD trailing (pas de daily loss limit sur Standard) ; Express = min jours reduit (~4 vs ~7) mais consistency plus stricte. Frais d'activation 120 $ au passage en finance. Split 80% en sim-funded puis 90% (upgrade lie aux retraits cumules / passage live selon sources). Comptes jusqu'a 250K$. Localisation exacte incertaine (sources partagees entre New York et LLC Floride).",
    sources: [
      'https://damnpropfirms.com/futures-prop-firms/funded-futures-network/',
      'https://bestpropfirmguide.com/firm/funded-futures-network/',
      'https://proptradingauthority.com/reviews/funded-futures-network-review/',
    ],
    lastVerified: '2026-07-26',
  },
];
